import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import { TableSkeleton } from "@/components/Skeleton";
import AppointmentActions from "@/components/AppointmentActions";
import { isArrived } from "@/lib/arrival";
import {
  ArrowRight, CheckCircle2, ClipboardList, FlaskConical, MessageSquare,
  UserPlus, CalendarClock, Sun, DoorOpen,
} from "lucide-react";

/** Today — one screen instead of five.
 *
 *  Care items only. No revenue reaches this surface: the person reading it is deciding
 *  who to help next, not how the month went. That lives in the owner snapshot.
 *
 *  The Unassigned section is not decoration. A patient belongs to nobody at the exact
 *  moment their cadence clock starts, and work that belongs to nobody must not be
 *  invisible to everybody — so it shows on every clinician's Today until someone takes it.
 */

const SECTIONS = [
  { key: "panel", label: "Your patients", icon: ClipboardList,
    empty: "Nobody on your panel needs chasing today." },
  { key: "unassigned", label: "Unassigned", icon: UserPlus,
    empty: "Every patient has a clinician.",
    note: "Nobody owns these yet. Take one and it moves to your list." },
  { key: "labs", label: "Labs to sign off", icon: FlaskConical,
    empty: "No results waiting on a signature." },
  { key: "requests", label: "Booking requests", icon: CalendarClock,
    empty: "No requests waiting." },
  { key: "messages", label: "Waiting on a reply", icon: MessageSquare,
    empty: "Nobody is waiting to hear back." },
];

function Row({ row, onTake, taking }) {
  const navigate = useNavigate();
  const overdue = row.state === "overdue";
  return (
    <li className="py-3 flex flex-wrap items-start justify-between gap-3"
        data-testid={`work-row-${row.kind}-${row.patient_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {row.patient_name}
          <span className="text-muted-foreground font-normal"> · {row.what}</span>
          {overdue && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                  data-testid={`work-overdue-${row.patient_id}`}>Overdue</span>
          )}
        </p>
        <p className="text-sm text-muted-foreground mt-0.5">{row.why}</p>
        <p className="text-sm font-medium mt-1 inline-flex items-center gap-1.5">
          <ArrowRight className="w-3.5 h-3.5 text-emerald-600" /> {row.action}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {onTake && (
          <button data-testid={`work-take-${row.patient_id}`} disabled={taking}
                  onClick={() => onTake(row)}
                  className="text-xs font-medium border border-border rounded-md px-2.5 py-1.5 hover:bg-secondary">
            {taking ? "Taking…" : "Take"}
          </button>
        )}
        <button data-testid={`work-go-${row.kind}-${row.patient_id}`}
                onClick={() => navigate(row.link)}
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-primary/90">
          Open
        </button>
      </div>
    </li>
  );
}

/** Today's visits, with the one action the front desk needs from this screen.
 *
 *  Arrival lives here and on /schedule because those are the two screens somebody is
 *  actually looking at when a patient walks in. It is deliberately NOT a worklist
 *  section: the worklist is "things to chase", and a visit happening in twenty minutes
 *  is not something anybody is behind on.
 */
function ArrivalsSection({ visits, error, onRetry, onChanged }) {
  const live = visits.filter((a) => a.status === "scheduled");
  if (!live.length && !error) return null;
  const waiting = live.filter(isArrived).length;

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="today-section-arrivals">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
          <DoorOpen className="w-[18px] h-[18px] text-muted-foreground" />
        </div>
        <h2 className="font-heading text-base font-semibold">Today's visits</h2>
        {live.length > 0 && (
          <span className="text-xs font-semibold tabular-nums bg-secondary text-secondary-foreground rounded-full px-2 py-0.5"
                data-testid="today-count-arrivals">{live.length}</span>
        )}
        {waiting > 0 && (
          <span className="text-xs font-medium text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                data-testid="today-count-arrived">{waiting} here</span>
        )}
      </div>

      <ErrorRetry
        error={error}
        variant="inline"
        onRetry={onRetry}
        note="Today's visits couldn't be loaded, so this list may be incomplete."
        testid="today-arrivals-error"
      />

      {live.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 ml-[42px] inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> No visits left today.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {live.map((a) => (
            <li key={a.id} className="py-3 flex flex-wrap items-start justify-between gap-3"
                data-testid={`today-visit-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  <span className="tabular-nums">{a.time}</span>
                  <span className="text-muted-foreground font-normal"> · {a.patient_name || "Patient"}</span>
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">{a.type}</p>
              </div>
              <AppointmentActions appt={a} onChanged={onChanged} className="shrink-0" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Today() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [taking, setTaking] = useState("");
  const [visits, setVisits] = useState([]);
  const [visitsErr, setVisitsErr] = useState(null);

  const load = useCallback(() => {
    api.get("/worklist/today")
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => { setData({ sections: {}, counts: {}, total: 0 }); setError(e); });
  }, []);

  /* Its own state and its own error, rather than folded into the worklist: a failure
     here must not make the worklist look empty, and an empty visit list must not look
     like a failed request. */
  const loadVisits = useCallback(() => {
    api.get("/appointments", { params: { scope: "today" } })
      .then((r) => { setVisits(r.data.appointments || []); setVisitsErr(null); })
      .catch((e) => setVisitsErr(e));
  }, []);

  useEffect(() => { load(); loadVisits(); }, [load, loadVisits]);

  const take = async (row) => {
    setTaking(row.patient_id);
    try {
      await api.put(`/patients/${row.patient_id}/provider`,
                    { primary_provider_id: user?.id });
      load();
    } catch (e) { setError(e); }
    setTaking("");
  };

  if (!data) return <div className="space-y-4"><TableSkeleton rows={6} /></div>;

  const nothingToDo = data.total === 0;

  return (
    <div className="space-y-6" data-testid="today-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Today</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {nothingToDo
            ? "Nothing needs chasing right now."
            : `${data.total} thing${data.total === 1 ? "" : "s"} to pick up — patients first.`}
        </p>
      </div>

      {/* The empty-sections fallback above makes a failure look like "you're clear",
          which on a care worklist is the most dangerous thing this screen can say. */}
      <ErrorRetry
        error={error}
        variant="inline"
        onRetry={() => { setError(null); load(); }}
        note="Your worklist couldn't be loaded — don't read an empty list as nothing to do."
        testid="today-error"
      />

      <ArrivalsSection
        visits={visits}
        error={visitsErr}
        onRetry={() => { setVisitsErr(null); loadVisits(); }}
        onChanged={() => { loadVisits(); load(); }}
      />

      {nothingToDo && (
        /* "No chasing to do", not "You're clear": Today's visits renders ABOVE this, so
           a full-page "You're clear" sat on top of a list of four appointments and read
           as a contradiction. The worklist being empty is a narrower claim than the day
           being empty, and the heading now says the narrower thing. */
        <EmptyState icon={Sun} title="No chasing to do"
                    description="No overdue care, no labs waiting, no unanswered messages."
                    testid="today-empty" />
      )}

      {SECTIONS.map(({ key, label, icon: Icon, empty, note }) => {
        const rows = data.sections?.[key] || [];
        if (nothingToDo) return null;
        return (
          <div key={key} className="bg-card border border-border rounded-lg p-5"
               data-testid={`today-section-${key}`}>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                <Icon className="w-[18px] h-[18px] text-muted-foreground" />
              </div>
              <h2 className="font-heading text-base font-semibold">{label}</h2>
              {rows.length > 0 && (
                <span className="text-xs font-semibold tabular-nums bg-secondary text-secondary-foreground rounded-full px-2 py-0.5"
                      data-testid={`today-count-${key}`}>{rows.length}</span>
              )}
            </div>
            {note && rows.length > 0 && (
              <p className="text-xs text-muted-foreground mb-2 ml-[42px]">{note}</p>
            )}
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2 ml-[42px] inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {empty}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {rows.map((row) => (
                  <Row key={`${row.kind}-${row.patient_id}-${row.what}`} row={row}
                       onTake={key === "unassigned" ? take : null}
                       taking={taking === row.patient_id} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
